/**
 * propertyDefsModel — user-defined task properties, as data instead of code.
 *
 * THE WHOLE DESIGN IN ONE PARAGRAPH. A property the user invents ("Room",
 * "Cost") cannot be a free-form key on a map, because Firestore cannot index
 * arbitrary map keys — every sort and filter would fall back to the client. So a
 * property is a LABEL pointed at one of a fixed set of REAL fields: `sel1..sel5`,
 * `num1..num3`, `date1..date3`, `txt1..txt5`, `chk1..chk3`. Those field paths are
 * fixed, so Firestore indexes and orders them natively; the definitions stored in
 * `propertyDefs/{spaceKey}` are what give them meaning. User-defined properties
 * AND server-side sorting, which a map cannot offer.
 *
 * THE SLOT IS THE KEY, AND THE LABEL IS JUST A LABEL. `slot` never changes once
 * assigned; renaming a property rewrites `label` alone. This is the single most
 * important rule here: if the key were the label, renaming "Room" to "Area"
 * would orphan every stored value on every item, silently, with no error. Notion
 * gets this right and hand-rolled versions usually do not.
 *
 * DELETE IS ARCHIVE, AND SLOTS ARE NOT REUSED. Archiving hides a property and
 * KEEPS its values, so a misclick is recoverable. Genuinely freeing a slot means
 * clearing that field on every item first — a real data operation, not a UI
 * toggle — so `reclaimSlot()` exists and deliberately requires the caller to have
 * done the purge. Reusing a slot without it means a brand-new "Cost" property
 * showing numbers left over from the "Budget" property somebody archived last
 * year.
 *
 * The cost of this approach, stated plainly: a fixed cap per type. Five selects,
 * three numbers. That is the price of indexability, and it is the trade the
 * "efficient path for tracking and sorting" decision bought.
 */

/** The fixed, indexable field slots each property type can occupy. */
export const SLOTS = {
	select: ['sel1', 'sel2', 'sel3', 'sel4', 'sel5'],
	number: ['num1', 'num2', 'num3'],
	date: ['date1', 'date2', 'date3'],
	text: ['txt1', 'txt2', 'txt3', 'txt4', 'txt5'],
	checkbox: ['chk1', 'chk2', 'chk3'],
};

export const PROPERTY_TYPES = Object.keys(SLOTS);

export const TYPE_META = {
	select: { label: 'Select', icon: 'fa-list-dropdown', hasOptions: true },
	number: { label: 'Number', icon: 'fa-hashtag', hasOptions: false },
	date: { label: 'Date', icon: 'fa-calendar', hasOptions: false },
	text: { label: 'Text', icon: 'fa-font', hasOptions: false },
	checkbox: { label: 'Checkbox', icon: 'fa-square-check', hasOptions: false },
};

export const MAX_LABEL = 40;

/** Every slot name, flattened — useful for stripping property fields off a doc. */
export const ALL_SLOTS = Object.values(SLOTS).flat();

/** Which type a slot belongs to, or null if it isn't a property slot at all. */
export function typeOfSlot(slot) {
	const hit = PROPERTY_TYPES.find((t) => SLOTS[t].includes(slot));
	return hit || null;
}

/**
 * Normalize whatever came back from Firestore into a clean definition list.
 *
 * Defensive on purpose: this document is small, hand-editable in the console,
 * and read by every task surface. A malformed entry is DROPPED rather than
 * rendered — a property with no slot has nowhere to read from, and one with an
 * unknown type has no editor, so showing either would produce a control that
 * silently does nothing.
 */
export function normalizeDefs(raw) {
	const list = Array.isArray(raw) ? raw : Array.isArray(raw?.props) ? raw.props : [];
	const seen = new Set();

	return list
		.filter((d) => d && typeof d === 'object')
		.map((d) => ({
			slot: typeof d.slot === 'string' ? d.slot : null,
			label: typeof d.label === 'string' ? d.label.trim() : '',
			type: d.type,
			options: Array.isArray(d.options)
				? d.options.map((o) => String(o).trim()).filter(Boolean)
				: [],
			order: Number.isFinite(d.order) ? d.order : 0,
			archived: d.archived === true,
		}))
		.filter((d) => {
			if (!d.slot || !d.label) return false;
			if (typeOfSlot(d.slot) !== d.type) return false; // slot/type disagreement
			if (seen.has(d.slot)) return false; // duplicate slot claim
			seen.add(d.slot);
			return true;
		})
		.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

/** The properties a surface should actually show. */
export function activeDefs(defs) {
	return normalizeDefs(defs).filter((d) => !d.archived);
}

/**
 * The next free slot for a type, or null when that type is exhausted.
 * ARCHIVED definitions still hold their slot — that is the point of archiving.
 */
export function nextFreeSlot(defs, type) {
	if (!SLOTS[type]) return null;
	const taken = new Set(normalizeDefs(defs).map((d) => d.slot));
	return SLOTS[type].find((s) => !taken.has(s)) || null;
}

/** How many slots of each type remain — for the editor's "3 of 5 used" line. */
export function slotUsage(defs) {
	const taken = new Set(normalizeDefs(defs).map((d) => d.slot));
	return PROPERTY_TYPES.reduce((acc, type) => {
		const used = SLOTS[type].filter((s) => taken.has(s)).length;
		acc[type] = { used, total: SLOTS[type].length, free: SLOTS[type].length - used };
		return acc;
	}, {});
}

/**
 * Validate a proposed label against the existing set.
 * Duplicate labels are rejected because the label is the ONLY thing the user
 * sees — two columns both called "Room" are indistinguishable in every surface,
 * even though their slots differ.
 */
export function validateLabel(defs, label, { ignoreSlot = null } = {}) {
	const trimmed = String(label ?? '').trim();
	if (!trimmed) return 'Give the property a name.';
	if (trimmed.length > MAX_LABEL) return `Keep it under ${MAX_LABEL} characters.`;
	const clash = normalizeDefs(defs).some(
		(d) =>
			d.slot !== ignoreSlot &&
			!d.archived &&
			d.label.toLowerCase() === trimmed.toLowerCase()
	);
	if (clash) return 'A property with that name already exists.';
	return null;
}

/**
 * Add a property. Returns { defs, error } — never throws, because every failure
 * here is a thing the editor needs to say out loud rather than a bug.
 */
export function addProperty(defs, { label, type, options = [] }) {
	const current = normalizeDefs(defs);
	if (!SLOTS[type]) return { defs: current, error: 'Unknown property type.' };

	const labelError = validateLabel(current, label);
	if (labelError) return { defs: current, error: labelError };

	const slot = nextFreeSlot(current, type);
	if (!slot) {
		const { total } = slotUsage(current)[type];
		return {
			defs: current,
			error: `All ${total} ${TYPE_META[type].label.toLowerCase()} properties are in use. Archive one first.`,
		};
	}

	const next = [
		...current,
		{
			slot,
			label: String(label).trim(),
			type,
			options: TYPE_META[type].hasOptions ? dedupeOptions(options) : [],
			order: current.length,
			archived: false,
		},
	];
	return { defs: next, error: null };
}

function dedupeOptions(options) {
	const seen = new Set();
	return (Array.isArray(options) ? options : [])
		.map((o) => String(o).trim())
		.filter((o) => {
			if (!o) return false;
			const k = o.toLowerCase();
			if (seen.has(k)) return false;
			seen.add(k);
			return true;
		});
}

/** Rename / retype-options. The slot is untouched, so stored values survive. */
export function updateProperty(defs, slot, changes = {}) {
	const current = normalizeDefs(defs);
	const target = current.find((d) => d.slot === slot);
	if (!target) return { defs: current, error: 'That property no longer exists.' };

	if (changes.label !== undefined) {
		const labelError = validateLabel(current, changes.label, { ignoreSlot: slot });
		if (labelError) return { defs: current, error: labelError };
	}

	// The TYPE is deliberately immutable. Changing it would reinterpret every
	// stored value — a date column read as a number is not a migration anyone
	// wants to happen from a dropdown.
	const next = current.map((d) =>
		d.slot === slot
			? {
					...d,
					label: changes.label !== undefined ? String(changes.label).trim() : d.label,
					options: TYPE_META[d.type].hasOptions
						? changes.options !== undefined
							? dedupeOptions(changes.options)
							: d.options
						: [],
			  }
			: d
	);
	return { defs: next, error: null };
}

/** Archive (the safe delete) — hides the property, KEEPS its values and slot. */
export function archiveProperty(defs, slot) {
	const next = normalizeDefs(defs).map((d) =>
		d.slot === slot ? { ...d, archived: true } : d
	);
	return { defs: next, error: null };
}

export function restoreProperty(defs, slot) {
	const current = normalizeDefs(defs);
	const target = current.find((d) => d.slot === slot);
	if (!target) return { defs: current, error: 'That property no longer exists.' };
	// Restoring can collide with a label created while it was archived.
	const labelError = validateLabel(current, target.label, { ignoreSlot: slot });
	if (labelError) return { defs: current, error: labelError };
	return {
		defs: current.map((d) => (d.slot === slot ? { ...d, archived: false } : d)),
		error: null,
	};
}

/**
 * Genuinely free a slot for reuse. NOT wired to any UI, and deliberately so.
 *
 * The definition is only half the property — the other half is the value sitting
 * on every task at `task[slot]`. Dropping the definition without clearing those
 * leaves a new property inheriting the old one's data: a fresh "Cost" showing
 * numbers from an archived "Budget". The caller must purge the field across the
 * collection FIRST; this only removes the definition.
 */
export function reclaimSlot(defs, slot, { valuesPurged = false } = {}) {
	if (!valuesPurged) {
		return {
			defs: normalizeDefs(defs),
			error: 'Clear this property\'s values from every task before reusing its slot.',
		};
	}
	return { defs: normalizeDefs(defs).filter((d) => d.slot !== slot), error: null };
}

/** Reorder — `order` is what the editor drags and every surface reads. */
export function reorderProperties(defs, orderedSlots) {
	const current = normalizeDefs(defs);
	const rank = new Map(orderedSlots.map((s, i) => [s, i]));
	return {
		defs: current
			.map((d) => ({ ...d, order: rank.has(d.slot) ? rank.get(d.slot) : current.length }))
			.sort((a, b) => a.order - b.order),
		error: null,
	};
}

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------

/** Read one property's value off a task, coerced to its declared type. */
export function readValue(task, def) {
	const raw = task?.[def?.slot];
	if (raw === undefined || raw === null) return null;
	switch (def.type) {
		case 'number':
			return Number.isFinite(raw) ? raw : null;
		case 'checkbox':
			return raw === true;
		case 'date':
			return toDateValue(raw);
		case 'select': {
			const v = String(raw);
			// An option removed from the definition leaves values behind. Showing
			// the orphan is right — hiding it would look like data loss.
			return v || null;
		}
		default:
			return String(raw);
	}
}

/**
 * A DATE PROPERTY IS A CALENDAR DATE, and this repo has already been bitten once
 * by treating one as an instant: the Notion import stored `dueAt` at midnight
 * UTC, local getters read it as the previous evening, and a real Sept 1 deadline
 * rendered as Aug 31 (see spaceItemsModel). Same discipline here, applied at
 * BOTH ends so it can't drift — the `<input type="date">` value is stored at
 * midnight UTC, and it is formatted back out with UTC getters.
 */
/** Firestore Timestamp | Date | null -> Date | null. Local to this module —
    spaceItemsModel has its own for the legacy item shape; they are deliberately
    not shared, because the two models describe different documents. */
export function toDateValue(value) {
	if (!value) return null;
	if (typeof value.toDate === 'function') return value.toDate();
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
	if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
	return null;
}

export function dateInputToValue(str) {
	if (!str) return null;
	const [y, m, d] = String(str).split('-').map(Number);
	if (!y || !m || !d) return null;
	return new Date(Date.UTC(y, m - 1, d));
}

/** Date -> the `YYYY-MM-DD` an <input type="date"> expects, read in UTC. */
export function valueToDateInput(value) {
	const d = toDateValue(value);
	if (!d) return '';
	const m = `${d.getUTCMonth() + 1}`.padStart(2, '0');
	const day = `${d.getUTCDate()}`.padStart(2, '0');
	return `${d.getUTCFullYear()}-${m}-${day}`;
}

/**
 * Build the Firestore patch for setting one property.
 * Empty input clears the field to null rather than deleting it, so the shape of
 * a task document stays predictable.
 */
export function writeValue(def, raw) {
	if (!def?.slot) return {};
	const empty = raw === '' || raw === undefined || raw === null;
	switch (def.type) {
		case 'number': {
			if (empty) return { [def.slot]: null };
			const n = Number(raw);
			return { [def.slot]: Number.isFinite(n) ? n : null };
		}
		case 'checkbox':
			return { [def.slot]: raw === true };
		case 'date':
			if (empty) return { [def.slot]: null };
			// Accept the input's string OR an already-built Date/Timestamp.
			return {
				[def.slot]: typeof raw === 'string' ? dateInputToValue(raw) : toDateValue(raw),
			};
		default:
			return { [def.slot]: empty ? null : String(raw) };
	}
}

const MONTH_SHORT = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Display string for a value — used by cards and the detail panel. */
export function formatValue(task, def) {
	const v = readValue(task, def);
	if (v === null) return '';
	switch (def.type) {
		case 'checkbox':
			return v ? 'Yes' : 'No';
		case 'date':
			// UTC getters, deliberately — see dateInputToValue above.
			return `${MONTH_SHORT[v.getUTCMonth()]} ${v.getUTCDate()}`;
		case 'number':
			return String(v);
		default:
			return String(v);
	}
}

/** Definitions that actually carry a value on this task — what a card renders. */
export function filledDefs(task, defs) {
	return activeDefs(defs).filter((d) => {
		const v = readValue(task, d);
		// An unchecked checkbox is a real answer but not worth a chip on a card.
		if (d.type === 'checkbox') return v === true;
		return v !== null && v !== '';
	});
}
